/**
 * Symbol Data Utilities
 * Organize and categorize LaTeX symbols for the symbol picker
 */

import { getUnicodeMathSymbols, type UnicodeMathSymbol } from '@/utils/latexDataLoader'

export type SymbolCategory = 
  | 'greek' 
  | 'operators' 
  | 'arrows' 
  | 'relations' 
  | 'sets' 
  | 'delimiters' 
  | 'misc'

export interface CategorizedSymbol extends UnicodeMathSymbol {
  category: SymbolCategory
}

/**
 * Categorize a symbol based on its command name and documentation
 */
function categorizeSymbol(symbol: UnicodeMathSymbol): SymbolCategory {
  const cmd = symbol.command.toLowerCase()
  const doc = (symbol.documentation || '').toLowerCase()
  
  // Greek letters - check for common Greek letter names
  const greekPatterns = [
    'alpha', 'beta', 'gamma', 'delta', 'epsilon', 'theta', 'lambda', 'mu', 'pi',
    'sigma', 'phi', 'omega', 'psi', 'chi', 'xi', 'eta', 'zeta', 'iota', 'kappa',
    'nu', 'rho', 'tau', 'upsilon', 'omicron', 'digamma', 'var'
  ]
  if (greekPatterns.some(pattern => cmd.includes(pattern)) || doc.includes('greek')) {
    return 'greek'
  }
  
  // Arrows - check for arrow-related commands
  const arrowPatterns = [
    'arrow', 'rightarrow', 'leftarrow', 'uparrow', 'downarrow', 'mapsto', 'to',
    'leadsto', 'hookrightarrow', 'hookleftarrow', 'nearrow', 'searrow', 'swarrow', 'nwarrow'
  ]
  if (arrowPatterns.some(pattern => cmd.includes(pattern)) || doc.includes('arrow')) {
    return 'arrows'
  }
  
  // Relations - comparison and equality operators
  const relationPatterns = [
    'leq', 'geq', 'eq', 'neq', 'approx', 'equiv', 'sim', 'cong', 'propto',
    'prec', 'succ', 'preceq', 'succeq', 'll', 'gg', 'parallel', 'perp',
    'doteq', 'simeq', 'asymp', 'models', 'vdash', 'dashv'
  ]
  if (relationPatterns.some(pattern => cmd.includes(pattern)) || 
      doc.includes('relation') || doc.includes('equal') || doc.includes('comparison')) {
    return 'relations'
  }
  
  // Operators - mathematical operators (check before sets to avoid conflicts)
  const operatorPatterns = [
    'sum', 'int', 'prod', 'lim', 'max', 'min', 'operator', 'nabla', 'partial',
    'oplus', 'ominus', 'otimes', 'odot', 'oslash', 'bigcup', 'bigcap', 'bigvee', 'bigwedge',
    'coprod', 'oint', 'iint', 'iiint', 'sqrt', 'log', 'ln', 'sin', 'cos', 'tan',
    'det', 'dim', 'exp', 'inf', 'sup', 'gcd', 'lcm', 'hom', 'ker'
  ]
  // Check for exact matches or word boundaries to avoid false positives
  if (operatorPatterns.some(pattern => {
    const regex = new RegExp(`^${pattern}$|\\b${pattern}\\b`)
    return regex.test(cmd)
  }) || doc.includes('operator') || doc.includes('integral') || doc.includes('summation')) {
    return 'operators'
  }
  
  // Sets - set theory symbols (check after operators)
  const setPatterns = [
    'notin', 'subset', 'superset', 'cup', 'cap', 'emptyset', 'set',
    'subseteq', 'supseteq', 'setminus', 'complement', 'sqcup', 'sqcap'
  ]
  // Special handling for 'in' - only if it's exactly 'in' or 'notin', not 'int' or 'sin'
  if (cmd === 'in' || setPatterns.some(pattern => {
    const regex = new RegExp(`^${pattern}$|\\b${pattern}\\b`)
    return regex.test(cmd)
  }) || doc.includes('set')) {
    return 'sets'
  }
  
  // Delimiters - brackets, parentheses, etc.
  const delimiterPatterns = [
    'paren', 'bracket', 'brace', 'vert', 'norm', 'lvert', 'rvert', 'lVert', 'rVert',
    'left', 'right', 'lfloor', 'rfloor', 'lceil', 'rceil', 'langle', 'rangle'
  ]
  if (delimiterPatterns.some(pattern => cmd.includes(pattern)) || doc.includes('delimiter')) {
    return 'delimiters'
  }
  
  return 'misc'
}

/**
 * Priority list of common symbols (shown first in their categories)
 */
const PRIORITY_SYMBOLS: Record<SymbolCategory, string[]> = {
  greek: [
    'alpha', 'beta', 'gamma', 'delta', 'epsilon', 'theta', 'lambda', 'mu', 'pi',
    'sigma', 'phi', 'omega', 'psi', 'chi', 'xi', 'eta', 'zeta', 'iota', 'kappa',
    'nu', 'rho', 'tau', 'upsilon', 'Gamma', 'Delta', 'Theta', 'Lambda', 'Pi',
    'Sigma', 'Phi', 'Omega', 'Psi', 'Xi'
  ],
  operators: [
    'sum', 'int', 'prod', 'lim', 'max', 'min', 'nabla', 'partial', 'sqrt',
    'log', 'ln', 'sin', 'cos', 'tan', 'exp', 'det', 'dim', 'inf', 'sup'
  ],
  arrows: [
    'rightarrow', 'leftarrow', 'uparrow', 'downarrow', 'mapsto', 'to', 'leftrightarrow',
    'Rightarrow', 'Leftarrow', 'Leftrightarrow', 'hookrightarrow', 'hookleftarrow'
  ],
  relations: [
    'leq', 'geq', 'eq', 'neq', 'approx', 'equiv', 'sim', 'cong', 'propto',
    'prec', 'succ', 'preceq', 'succeq', 'parallel', 'perp', 'doteq', 'simeq'
  ],
  sets: [
    'in', 'notin', 'subset', 'superset', 'cup', 'cap', 'emptyset', 'subseteq',
    'supseteq', 'setminus', 'complement', 'times', 'sqcup', 'sqcap'
  ],
  delimiters: [
    'left', 'right', 'lvert', 'rvert', 'lVert', 'rVert', 'lfloor', 'rfloor',
    'lceil', 'rceil', 'langle', 'rangle', 'vert', 'Vert'
  ],
  misc: []
}

/**
 * Get priority score for a symbol (higher = more common)
 * Exported for use in UI components
 */
export function getSymbolPriority(symbol: CategorizedSymbol): number {
  const priorityList = PRIORITY_SYMBOLS[symbol.category] || []
  const cmd = symbol.command.toLowerCase()
  const index = priorityList.findIndex(p => cmd === p.toLowerCase() || cmd.includes(p.toLowerCase()))
  if (index >= 0) {
    return 1000 - index // Higher priority for earlier items
  }
  return 0
}

/**
 * Sort symbols within a category
 */
function sortSymbols(symbols: CategorizedSymbol[]): CategorizedSymbol[] {
  return symbols.sort((a, b) => {
    // First by priority (common symbols first)
    const priorityDiff = getSymbolPriority(b) - getSymbolPriority(a)
    if (priorityDiff !== 0) return priorityDiff
    
    // Then alphabetically by command name
    return a.command.localeCompare(b.command, undefined, { sensitivity: 'base' })
  })
}

/**
 * Get all symbols organized by category, sorted and organized
 */
export function getCategorizedSymbols(): Record<SymbolCategory, CategorizedSymbol[]> {
  const symbols = getUnicodeMathSymbols()
  const categorized: Record<SymbolCategory, CategorizedSymbol[]> = {
    greek: [],
    operators: [],
    arrows: [],
    relations: [],
    sets: [],
    delimiters: [],
    misc: []
  }
  
  symbols.forEach(symbol => {
    const category = categorizeSymbol(symbol)
    categorized[category].push({
      ...symbol,
      category
    })
  })
  
  // Sort each category
  Object.keys(categorized).forEach(category => {
    categorized[category as SymbolCategory] = sortSymbols(categorized[category as SymbolCategory])
  })
  
  return categorized
}

/**
 * Search symbols by query (searches command name, detail, and documentation)
 * Results are sorted by relevance (exact matches first, then priority)
 */
export function searchSymbols(query: string): CategorizedSymbol[] {
  if (!query.trim()) return []
  
  const lowerQuery = query.toLowerCase()
  const symbols = getUnicodeMathSymbols()
  
  const results = symbols
    .map(symbol => ({
      ...symbol,
      category: categorizeSymbol(symbol)
    }))
    .filter(symbol => {
      const cmd = symbol.command.toLowerCase()
      const detail = (symbol.detail || '').toLowerCase()
      const doc = (symbol.documentation || '').toLowerCase()
      
      return cmd.includes(lowerQuery) || 
             detail.includes(lowerQuery) || 
             doc.includes(lowerQuery)
    })
    .map(symbol => ({
      ...symbol,
      // Calculate relevance score
      relevance: calculateRelevance(symbol, lowerQuery)
    }))
    .sort((a, b) => {
      // Sort by relevance (exact matches first)
      if (b.relevance !== a.relevance) {
        return b.relevance - a.relevance
      }
      // Then by priority
      const priorityDiff = getSymbolPriority(b) - getSymbolPriority(a)
      if (priorityDiff !== 0) return priorityDiff
      // Then alphabetically
      return a.command.localeCompare(b.command, undefined, { sensitivity: 'base' })
    })
  
  // Remove relevance property before returning
  return results.map(({ relevance, ...symbol }) => symbol)
}

/**
 * Calculate relevance score for search results
 */
function calculateRelevance(symbol: CategorizedSymbol, query: string): number {
  const cmd = symbol.command.toLowerCase()
  const detail = (symbol.detail || '').toLowerCase()
  const doc = (symbol.documentation || '').toLowerCase()
  
  let score = 0
  
  // Exact command match gets highest score
  if (cmd === query) {
    score += 1000
  } else if (cmd.startsWith(query)) {
    score += 500
  } else if (cmd.includes(query)) {
    score += 100
  }
  
  // Detail match
  if (detail.includes(query)) {
    score += 50
  }
  
  // Documentation match
  if (doc.includes(query)) {
    score += 10
  }
  
  return score
}

/**
 * Get category display name
 */
export function getCategoryName(category: SymbolCategory): string {
  const names: Record<SymbolCategory, string> = {
    greek: 'Greek Letters',
    operators: 'Operators',
    arrows: 'Arrows',
    relations: 'Relations',
    sets: 'Sets',
    delimiters: 'Delimiters',
    misc: 'Other Symbols'
  }
  return names[category]
}

/**
 * Get category icon (using Unicode symbols)
 */
export function getCategoryIcon(category: SymbolCategory): string {
  const icons: Record<SymbolCategory, string> = {
    greek: 'α',
    operators: '∑',
    arrows: '→',
    relations: '=',
    sets: '∈',
    delimiters: '()',
    misc: '◊'
  }
  return icons[category]
}


